import React, { createRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, TextField, makeStyles } from '@material-ui/core';
import Autocomplete, {
  createFilterOptions,
} from '@material-ui/lab/Autocomplete';
import {
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  CheckBox as CheckBoxIcon,
} from '@material-ui/icons';
import VirtualListboxComponent from './VirtualListboxComponent';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
const useStyles = makeStyles({
  listbox: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
});

export function CustomAutocomplete(props) {
  const {
    value,
    multiple,
    label,
    required,
    autoFocus,
    getOptionLabel,
    renderOption,
    onChange,
    placeholder,
    onAddValue,
    error,
    errorText,
    variant,
    getOptionSize,
    ...restProps
  } = props;
  const allowCreate = useMemo(() => !!onAddValue, [onAddValue]);
  const classes = useStyles();
  const handleChange = useCallback(
    (_event, value) => {
      const val = multiple ? value[value.length - 1] : value;

      if (onAddValue && val?.inputValue) {
        onAddValue(val.inputValue);
      } else {
        onChange(value);
      }
    },
    [onChange, onAddValue, multiple]
  );
  const filterOptions = useCallback(
    (options, params) => {
      const filter = createFilterOptions();
      const filtered = filter(options, params);

      if (allowCreate && params.inputValue !== '') {
        filtered.push({
          inputValue: params.inputValue,
          displayCreate: `Add "${params.inputValue}"`,
        });
      }

      return filtered;
    },
    [allowCreate]
  );
  const wrappedValue = useMemo(() => (value === '' ? null : value), [value]);
  const wrappedGetOptionLabel = useMemo(
    () =>
      allowCreate
        ? (option) => {
            if (typeof option === 'string') {
              // value selected with enter, right from the input
              return option;
            }
            if (option.inputValue) {
              // value selected via "Add ..."
              return option.inputValue;
            }

            return getOptionLabel(option);
          }
        : getOptionLabel,
    [allowCreate, getOptionLabel]
  );
  const wrappedRenderOption = useMemo(
    () =>
      renderOption
        ? renderOption
        : (option, { selected }) => (
            <>
              {multiple && !option.displayCreate && (
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
              )}
              {(allowCreate && option.displayCreate) || getOptionLabel(option)}
            </>
          ),
    [allowCreate, getOptionLabel, multiple, renderOption]
  );
  const ref = createRef();

  return (
    <Autocomplete
      {...restProps}
      ref={ref}
      autoComplete
      autoHighlight
      ListboxComponent={VirtualListboxComponent}
      ListboxProps={{parentRef: ref, getOptionSize: getOptionSize}}
      classes={classes}
      selectOnFocus
      blurOnSelect={allowCreate && !multiple}
      clearOnBlur
      freeSolo={allowCreate}
      multiple={multiple}
      filterOptions={filterOptions}
      getOptionLabel={wrappedGetOptionLabel}
      disableCloseOnSelect={multiple}
      renderOption={wrappedRenderOption}
      renderInput={(params) => (
        <TextField
          {...params}
          variant={variant}
          label={label}
          required={required}
          autoFocus={autoFocus}
          placeholder={placeholder}
          error={error}
          helperText={errorText}
        />
      )}
      onChange={handleChange}
      value={wrappedValue}
    />
  );
}

CustomAutocomplete.propTypes = {
  /** Label for the text field */
  label: PropTypes.string.isRequired,
  /** The selected value */
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.array,
  ]),
  /** Array of selectable options */
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.object])
  ).isRequired,
  /** Function called when the selection is changed */
  onChange: PropTypes.func.isRequired,
  /** Function called when a new value is added */
  onAddValue: PropTypes.func,
  /** Function that returns string values to represent non string options */
  getOptionLabel: PropTypes.func,
  /** Not sure about this one... fix later */
  renderOption: PropTypes.func,
  /** Specifies whether this field is required or not */
  required: PropTypes.bool,
  /** allows for multiple selection */
  multiple: PropTypes.bool,
  autoFocus: PropTypes.bool,
  /** Provides a placeholder string to occupy the selection when nothing is selected */
  placeholder: PropTypes.string,
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  /** Defines the state in which the text field contains an invalid value */
  error: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  /** String displayed when the text field is in an error state */
  errorText: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  /** Defines the variant of text field to use: "standard", "outlined", etc. */
  variant: PropTypes.string,
  getOptionSize: PropTypes.func
};

CustomAutocomplete.defaultProps = {
  required: false,
  multiple: false,
  autoFocus: false,
  style: null,
  variant: 'outlined',
  getOptionLabel: (x) => x,
  getOptionSize: undefined,
  renderOption: null,
  value: null,
  placeholder: '',
  onAddValue: null,
  error: false,
  errorText: '',
};
